import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { on } from '@ember/modifier';
import { LinkTo } from '@ember/routing';
import { VStack } from 'tio-ui/components/layout';
import Heading from 'tio-ui/components/heading';
import Text from 'tio-ui/components/text';
import Chip from 'tio-ui/components/chip';
import PageHero from 'tio-ui/components/page-hero';
import SectionHeader from 'tio-ui/components/section-header';
import Button from 'tio-ui/components/button';
import Popover from 'tio-ui/components/popover';
import type { PopoverPlacement } from 'tio-ui/components/popover';
import type { PopoverIntent } from 'tio-ui/color-tokens';
import type { ChipIntent } from 'tio-ui/components/chip';
import {
  PhArrowLeft,
  PhChatCircle,
  PhInfo,
  PhCheckCircle,
  PhWarning,
  PhShieldWarning,
  PhLightning,
  PhTarget,
  PhArrowsOutCardinal,
  PhCompass,
  PhPalette,
  PhMouseSimple,
  PhGear,
  PhBell,
  PhUser,
  PhStack,
  PhSparkle,
  PhCursor,
  PhCaretDown,
  PhBookOpen,
} from 'ember-phosphor-icons';

// ---------------------------------------------------------------------------
// Popover Demo Page
// ---------------------------------------------------------------------------

/** All 12 placements laid out in a compass-style grid */
const PLACEMENTS: PopoverPlacement[] = [
  'top-start',
  'top',
  'top-end',
  'left-start',
  'left',
  'left-end',
  'right-start',
  'right',
  'right-end',
  'bottom-start',
  'bottom',
  'bottom-end',
];

interface IntentDemo {
  intent: PopoverIntent;
  chipIntent: ChipIntent;
  label: string;
  description: string;
  icon: typeof PhInfo;
}

const INTENTS: IntentDemo[] = [
  {
    intent: 'default',
    chipIntent: 'default',
    label: 'Default',
    description: 'Neutral informational popover for general context.',
    icon: PhChatCircle,
  },
  {
    intent: 'info',
    chipIntent: 'primary',
    label: 'Info',
    description: 'Informational callout to highlight helpful tips.',
    icon: PhInfo,
  },
  {
    intent: 'success',
    chipIntent: 'success',
    label: 'Success',
    description: 'Confirmation that an action completed successfully.',
    icon: PhCheckCircle,
  },
  {
    intent: 'warning',
    chipIntent: 'warning',
    label: 'Warning',
    description: 'Heads up — something needs attention.',
    icon: PhWarning,
  },
  {
    intent: 'danger',
    chipIntent: 'danger',
    label: 'Danger',
    description: 'Critical alert — this action is destructive.',
    icon: PhShieldWarning,
  },
];

export default class PopoverDemoPage extends Component {
  // ── Placement grid ──────────────────────────────────────────────

  placements = PLACEMENTS;
  intents = INTENTS;

  // ── Controlled popover ──────────────────────────────────────────

  @tracked controlledOpen = false;

  openControlled = () => {
    this.controlledOpen = true;
  };

  closeControlled = () => {
    this.controlledOpen = false;
  };

  toggleControlled = () => {
    this.controlledOpen = !this.controlledOpen;
  };

  <template>
    {{! template-lint-disable no-bare-strings }}
    <VStack class="w-full min-w-0">
      {{! Back navigation }}
      <div class="mb-4">
        <LinkTo @route="authenticated.reimagined.components" class="tio-demo-link">
          <PhArrowLeft @size={{18}} />
          Back to Components
        </LinkTo>
      </div>

      {{! Header }}
      <PageHero
        @title="Popover"
        @subtitle="Space-aware, animated popovers with intent theming, hover mode, named blocks, and automatic viewport-flip positioning."
        @icon={{PhChatCircle}}
        @gradient="cool"
        @decoration="dots"
      >
        <:actions>
          <Chip @intent="primary">
            <PhSparkle @size={{14}} class="mr-1" />
            Space-Aware
          </Chip>
          <Chip @intent="success">
            <PhTarget @size={{14}} class="mr-1" />
            12 Placements
          </Chip>
        </:actions>
      </PageHero>

      {{! ================================================================
            Section 1: Basic Click Popover
            ================================================================ }}
      <SectionHeader
        @title="Basic Click Popover"
        @subtitle="Click the trigger to toggle. Closes on outside-click, Escape, or scroll."
        @icon={{PhCursor}}
      />

      <div
        class="tio-bg-surface dark:bg-gray-900 rounded-xl border tio-border-default dark:border-gray-800 p-8"
      >
        <div class="tio-row-extra-loose flex-wrap">
          {{! Simple body-only }}
          <Popover @placement="bottom">
            <:trigger as |t|>
              <Button @intent="primary" {{t.register}} {{on "click" t.toggle}}>
                <PhChatCircle @size={{16}} class="mr-1.5" />
                Simple Popover
              </Button>
            </:trigger>
            <:body>
              <p>This is a basic click popover. It auto-closes when you click outside, press Escape,
                or scroll.</p>
            </:body>
          </Popover>

          {{! With header + body + footer }}
          <Popover @placement="bottom" @showCloseButton={{true}}>
            <:trigger as |t|>
              <Button
                @intent="default"
                @appearance="outlined"
                {{t.register}}
                {{on "click" t.toggle}}
              >
                <PhBookOpen @size={{16}} class="mr-1.5" />
                With All Blocks
              </Button>
            </:trigger>
            <:header>
              <div class="tio-row-tight">
                <PhInfo @size={{18}} class="tio-fg-info" />
                <span>Getting Started</span>
              </div>
            </:header>
            <:body>
              <p>This popover uses all three named blocks: header, body, and footer. The close
                button is also enabled.</p>
            </:body>
            <:footer>
              <div class="tio-flex-end gap-2">
                <Button @intent="default" @appearance="outlined" @size="sm">Learn More</Button>
                <Button @intent="primary" @size="sm">Got It</Button>
              </div>
            </:footer>
          </Popover>

          {{! With accent bar }}
          <Popover @placement="bottom" @intent="info" @showAccentBar={{true}}>
            <:trigger as |t|>
              <Button
                @intent="primary"
                @appearance="outlined"
                {{t.register}}
                {{on "click" t.toggle}}
              >
                <PhLightning @size={{16}} class="mr-1.5" />
                With Accent Bar
              </Button>
            </:trigger>
            <:body>
              <p class="font-medium mb-1">Pro tip</p>
              <p class="text-xs opacity-80">The accent bar highlights the edge nearest the trigger,
                giving a strong visual anchor to the source element.</p>
            </:body>
          </Popover>
        </div>
      </div>

      {{! ================================================================
            Section 2: Hover Mode
            ================================================================ }}
      <SectionHeader
        @title="Hover Tooltips"
        @subtitle="Set triggerMode='hover' for tooltip-style behavior. Includes a smart delay so moving from trigger to popover doesn't dismiss."
        @icon={{PhMouseSimple}}
      />

      <div
        class="tio-bg-surface dark:bg-gray-900 rounded-xl border tio-border-default dark:border-gray-800 p-8"
      >
        <div class="tio-row-extra-loose flex-wrap">
          <Popover @placement="top" @triggerMode="hover">
            <:trigger as |t|>
              <span
                {{t.register}}
                {{t.hoverHandlers}}
                class="tio-inline-row gap-1.5 px-3 py-1.5 rounded-lg tio-bg-sunken tio-text-sm-medium tio-fg-secondary cursor-default"
              >
                <PhBell @size={{16}} />
                Notifications
                <PhInfo @size={{14}} class="tio-fg-disabled" />
              </span>
            </:trigger>
            <:body>
              <p>Hover tooltips are great for contextual help. Move your cursor to this popover body
                — it stays open, thanks to the hover delay.</p>
            </:body>
          </Popover>

          <Popover @placement="top" @triggerMode="hover" @intent="success" @showAccentBar={{true}}>
            <:trigger as |t|>
              <span
                {{t.register}}
                {{t.hoverHandlers}}
                class="tio-inline-row gap-1.5 px-3 py-1.5 rounded-lg tio-bg-success-soft dark:bg-emerald-950 tio-text-sm-medium tio-fg-success-body dark:text-emerald-300 cursor-default"
              >
                <PhCheckCircle @size={{16}} />
                Status: Active
              </span>
            </:trigger>
            <:header>
              <div class="tio-row-tight">
                <PhCheckCircle @size={{16}} class="tio-fg-success" />
                <span>Account Active</span>
              </div>
            </:header>
            <:body>
              <p>Your tuition assistance account is active and in good standing. Next review date:
                March 15, 2026.</p>
            </:body>
          </Popover>

          <Popover @placement="right" @triggerMode="hover" @intent="warning">
            <:trigger as |t|>
              <span
                {{t.register}}
                {{t.hoverHandlers}}
                class="tio-inline-row gap-1.5 px-3 py-1.5 rounded-lg tio-bg-warning-soft dark:bg-amber-950 tio-text-sm-medium tio-fg-warning-body dark:text-amber-300 cursor-default"
              >
                <PhWarning @size={{16}} />
                3 items need attention
              </span>
            </:trigger>
            <:body>
              <ul class="space-y-1 text-xs">
                <li class="flex items-center gap-1.5">
                  <span class="w-1.5 h-1.5 rounded-full bg-amber-400"></span>
                  Missing transcript for Fall 2025
                </li>
                <li class="flex items-center gap-1.5">
                  <span class="w-1.5 h-1.5 rounded-full bg-amber-400"></span>
                  Grade report overdue (CS 401)
                </li>
                <li class="flex items-center gap-1.5">
                  <span class="w-1.5 h-1.5 rounded-full bg-amber-400"></span>
                  Enrollment verification expires Feb 28
                </li>
              </ul>
            </:body>
          </Popover>
        </div>
      </div>

      {{! ================================================================
            Section 3: All 12 Placements
            ================================================================ }}
      <SectionHeader
        @title="Placement Compass"
        @subtitle="All 12 placement options. Each auto-flips to the opposite side if there's not enough viewport space."
        @icon={{PhCompass}}
      />

      <div
        class="tio-bg-surface dark:bg-gray-900 rounded-xl border tio-border-default dark:border-gray-800 p-8"
      >
        {{! Compass-style grid layout }}
        <div class="grid grid-cols-5 gap-3 max-w-2xl mx-auto">
          {{! Row 1: top-start, top, top-end }}
          <div></div>
          <Popover @placement="top-start">
            <:trigger as |t|>
              <button
                type="button"
                {{t.register}}
                {{on "click" t.toggle}}
                class="tio-demo-placement-trigger"
              >top-start</button>
            </:trigger>
            <:body><p>Placed at <strong>top-start</strong>.</p></:body>
          </Popover>

          <Popover @placement="top">
            <:trigger as |t|>
              <button
                type="button"
                {{t.register}}
                {{on "click" t.toggle}}
                class="tio-demo-placement-trigger"
              >top</button>
            </:trigger>
            <:body><p>Placed at <strong>top</strong> (centered).</p></:body>
          </Popover>

          <Popover @placement="top-end">
            <:trigger as |t|>
              <button
                type="button"
                {{t.register}}
                {{on "click" t.toggle}}
                class="tio-demo-placement-trigger"
              >top-end</button>
            </:trigger>
            <:body><p>Placed at <strong>top-end</strong>.</p></:body>
          </Popover>
          <div></div>

          {{! Row 2: left-start, (empty center), right-start }}
          <Popover @placement="left-start">
            <:trigger as |t|>
              <button
                type="button"
                {{t.register}}
                {{on "click" t.toggle}}
                class="tio-demo-placement-trigger"
              >left-start</button>
            </:trigger>
            <:body><p>Placed at <strong>left-start</strong>.</p></:body>
          </Popover>
          <div></div>
          <div
            class="tio-row-center rounded-lg border-2 border-dashed tio-border-brand-soft dark:border-violet-800 text-violet-400 dark:text-violet-400"
          >
            <PhArrowsOutCardinal @size={{24}} />
          </div>
          <div></div>
          <Popover @placement="right-start">
            <:trigger as |t|>
              <button
                type="button"
                {{t.register}}
                {{on "click" t.toggle}}
                class="tio-demo-placement-trigger"
              >right-start</button>
            </:trigger>
            <:body><p>Placed at <strong>right-start</strong>.</p></:body>
          </Popover>

          {{! Row 3: left, (empty center), right }}
          <Popover @placement="left">
            <:trigger as |t|>
              <button
                type="button"
                {{t.register}}
                {{on "click" t.toggle}}
                class="tio-demo-placement-trigger"
              >left</button>
            </:trigger>
            <:body><p>Placed at <strong>left</strong> (centered).</p></:body>
          </Popover>
          <div></div>
          <div></div>
          <div></div>
          <Popover @placement="right">
            <:trigger as |t|>
              <button
                type="button"
                {{t.register}}
                {{on "click" t.toggle}}
                class="tio-demo-placement-trigger"
              >right</button>
            </:trigger>
            <:body><p>Placed at <strong>right</strong> (centered).</p></:body>
          </Popover>

          {{! Row 4: left-end, (empty center), right-end }}
          <Popover @placement="left-end">
            <:trigger as |t|>
              <button
                type="button"
                {{t.register}}
                {{on "click" t.toggle}}
                class="tio-demo-placement-trigger"
              >left-end</button>
            </:trigger>
            <:body><p>Placed at <strong>left-end</strong>.</p></:body>
          </Popover>
          <div></div>
          <div></div>
          <div></div>
          <Popover @placement="right-end">
            <:trigger as |t|>
              <button
                type="button"
                {{t.register}}
                {{on "click" t.toggle}}
                class="tio-demo-placement-trigger"
              >right-end</button>
            </:trigger>
            <:body><p>Placed at <strong>right-end</strong>.</p></:body>
          </Popover>

          {{! Row 5: bottom-start, bottom, bottom-end }}
          <div></div>
          <Popover @placement="bottom-start">
            <:trigger as |t|>
              <button
                type="button"
                {{t.register}}
                {{on "click" t.toggle}}
                class="tio-demo-placement-trigger"
              >bottom-start</button>
            </:trigger>
            <:body><p>Placed at <strong>bottom-start</strong>.</p></:body>
          </Popover>

          <Popover @placement="bottom">
            <:trigger as |t|>
              <button
                type="button"
                {{t.register}}
                {{on "click" t.toggle}}
                class="tio-demo-placement-trigger"
              >bottom</button>
            </:trigger>
            <:body><p>Placed at <strong>bottom</strong> (centered).</p></:body>
          </Popover>

          <Popover @placement="bottom-end">
            <:trigger as |t|>
              <button
                type="button"
                {{t.register}}
                {{on "click" t.toggle}}
                class="tio-demo-placement-trigger"
              >bottom-end</button>
            </:trigger>
            <:body><p>Placed at <strong>bottom-end</strong>.</p></:body>
          </Popover>
          <div></div>
        </div>

        <div class="mt-6 text-center">
          <Text @size="sm" @variant="muted">
            Try scrolling a trigger to the edge of the viewport — the popover auto-flips to the
            opposite side.
          </Text>
        </div>
      </div>

      {{! ================================================================
            Section 4: Space-Aware Flip Demo
            ================================================================ }}
      <SectionHeader
        @title="Space-Aware Flip"
        @subtitle="These triggers are placed at viewport edges. Watch each popover flip to avoid clipping."
        @icon={{PhArrowsOutCardinal}}
      />

      <div
        class="tio-bg-surface dark:bg-gray-900 rounded-xl border tio-border-default dark:border-gray-800 p-8"
      >
        <div class="tio-row-start-between">
          {{! Left edge — prefers left, should flip right }}
          <Popover @placement="left" @intent="info" @showAccentBar={{true}}>
            <:trigger as |t|>
              <Button
                @intent="primary"
                @appearance="outlined"
                @size="sm"
                {{t.register}}
                {{on "click" t.toggle}}
              >
                ← Prefers Left
              </Button>
            </:trigger>
            <:body>
              <p class="font-medium mb-1">Flipped!</p>
              <p class="text-xs opacity-80">This popover prefers
                <code class="text-xs tio-bg-sunken px-1 rounded">left</code>
                but flipped to
                <code class="text-xs tio-bg-sunken px-1 rounded">right</code>
                because there's no room on the left.</p>
            </:body>
          </Popover>

          {{! Center — normal placement }}
          <Popover @placement="top" @intent="success" @showAccentBar={{true}}>
            <:trigger as |t|>
              <Button
                @intent="success"
                @appearance="outlined"
                @size="sm"
                {{t.register}}
                {{on "click" t.toggle}}
              >
                ↑ Prefers Top
              </Button>
            </:trigger>
            <:body>
              <p>This one has room both ways, so it stays at the preferred
                <code class="text-xs tio-bg-sunken px-1 rounded">top</code>
                placement.</p>
            </:body>
          </Popover>

          {{! Right edge — prefers right, should flip left }}
          <Popover @placement="right" @intent="warning" @showAccentBar={{true}}>
            <:trigger as |t|>
              <Button
                @intent="warning"
                @appearance="outlined"
                @size="sm"
                {{t.register}}
                {{on "click" t.toggle}}
              >
                Prefers Right →
              </Button>
            </:trigger>
            <:body>
              <p class="font-medium mb-1">Flipped!</p>
              <p class="text-xs opacity-80">This popover prefers
                <code class="text-xs tio-bg-sunken px-1 rounded">right</code>
                but flipped to
                <code class="text-xs tio-bg-sunken px-1 rounded">left</code>
                because there's no room on the right.</p>
            </:body>
          </Popover>
        </div>
      </div>

      {{! ================================================================
            Section 5: Intent Gallery
            ================================================================ }}
      <SectionHeader
        @title="Intent Theming"
        @subtitle="Five built-in color intents, each with matching arrow, border, shadow, accent bar, and header styling."
        @icon={{PhPalette}}
      />

      <div
        class="tio-bg-surface dark:bg-gray-900 rounded-xl border tio-border-default dark:border-gray-800 p-8"
      >
        <div class="tio-row-top-loose flex-wrap">
          {{#each this.intents as |demo|}}
            <Popover
              @placement="bottom"
              @intent={{demo.intent}}
              @showAccentBar={{true}}
              @showCloseButton={{true}}
            >
              <:trigger as |t|>
                <button
                  type="button"
                  {{t.register}}
                  {{on "click" t.toggle}}
                  class="tio-row-tight px-4 py-2.5 rounded-xl border transition-all duration-150 tio-border-default tio-bg-surface dark:bg-gray-900 hover:shadow-md hover:-translate-y-0.5 tio-text-sm-medium tio-fg-secondary"
                >
                  <demo.icon @size={{18}} />
                  {{demo.label}}
                  <PhCaretDown @size={{12}} class="tio-fg-disabled" />
                </button>
              </:trigger>
              <:header>
                <div class="tio-row-tight">
                  <demo.icon @size={{16}} />
                  <span>{{demo.label}} Intent</span>
                </div>
              </:header>
              <:body>
                <p>{{demo.description}}</p>
              </:body>
              <:footer>
                <div class="tio-row-between">
                  <span class="text-xs tio-fg-disabled font-mono">intent="{{demo.intent}}"</span>
                  <Chip @intent={{demo.chipIntent}} @size="sm">{{demo.label}}</Chip>
                </div>
              </:footer>
            </Popover>
          {{/each}}
        </div>
      </div>

      {{! ================================================================
            Section 6: Controlled State
            ================================================================ }}
      <SectionHeader
        @title="Controlled Mode"
        @subtitle="Pass @isOpen, @onOpen, @onClose to control the popover externally. Useful for guided tours or step-by-step flows."
        @icon={{PhGear}}
      />

      <div
        class="tio-bg-surface dark:bg-gray-900 rounded-xl border tio-border-default dark:border-gray-800 p-8"
      >
        <div class="tio-row-loose flex-wrap">
          <Popover
            @placement="bottom"
            @intent="info"
            @isOpen={{this.controlledOpen}}
            @onClose={{this.closeControlled}}
            @onOpen={{this.openControlled}}
            @showCloseButton={{true}}
            @showAccentBar={{true}}
          >
            <:trigger as |t|>
              <Button @intent="primary" {{t.register}} {{on "click" t.toggle}}>
                {{if this.controlledOpen "Close" "Open"}}
                Controlled
              </Button>
            </:trigger>
            <:header>
              <div class="tio-row-tight">
                <PhGear @size={{16}} class="tio-fg-info" />
                <span>Controlled Popover</span>
              </div>
            </:header>
            <:body>
              <p>This popover's open state is controlled by the parent component via
                <code class="text-xs tio-bg-sunken px-1 rounded">@isOpen</code>.</p>
              <p class="mt-2 text-xs tio-fg-muted">You can also close it with the buttons below,
                Escape, or clicking outside.</p>
            </:body>
            <:footer>
              <div class="tio-flex-end gap-2">
                <Button
                  @intent="default"
                  @appearance="outlined"
                  @size="sm"
                  {{on "click" this.closeControlled}}
                >Close</Button>
              </div>
            </:footer>
          </Popover>

          <div class="tio-row text-sm tio-fg-muted">
            <span>State:</span>
            <Chip @intent={{if this.controlledOpen "success" "default"}} @size="sm">
              {{if this.controlledOpen "Open" "Closed"}}
            </Chip>
          </div>

          {{! External toggle buttons }}
          <div class="tio-flex-tight ml-auto">
            <Button
              @intent="success"
              @appearance="outlined"
              @size="sm"
              {{on "click" this.openControlled}}
            >
              Open Externally
            </Button>
            <Button
              @intent="danger"
              @appearance="outlined"
              @size="sm"
              {{on "click" this.closeControlled}}
            >
              Close Externally
            </Button>
          </div>
        </div>
      </div>

      {{! ================================================================
            Section 7: Rich Content Examples
            ================================================================ }}
      <SectionHeader
        @title="Rich Content"
        @subtitle="Popovers can contain complex layouts — user cards, notification lists, and interactive content."
        @icon={{PhStack}}
      />

      <div
        class="tio-bg-surface dark:bg-gray-900 rounded-xl border tio-border-default dark:border-gray-800 p-8"
      >
        <div class="tio-row-extra-loose flex-wrap">
          {{! User profile card popover }}
          <Popover
            @placement="bottom-start"
            @intent="default"
            @showCloseButton={{true}}
            @maxWidth={{320}}
          >
            <:trigger as |t|>
              <button
                type="button"
                {{t.register}}
                {{on "click" t.toggle}}
                class="tio-row-tight px-3 py-1.5 rounded-full tio-bg-brand-soft dark:bg-violet-900 tio-fg-brand dark:text-violet-300 tio-text-sm-medium hover:bg-violet-200 dark:hover:bg-violet-800 transition-colors"
              >
                <PhUser @size={{16}} />
                Sarah Chen
              </button>
            </:trigger>
            <:header>
              <div class="tio-row">
                <div
                  class="w-10 h-10 rounded-full tio-bg-brand-accent tio-row-center tio-fg-inverse tio-text-sm-bold"
                >SC</div>
                <div>
                  <p class="font-semibold">Sarah Chen</p>
                  <p class="text-xs tio-fg-muted">Engineering Manager</p>
                </div>
              </div>
            </:header>
            <:body>
              <div class="space-y-2 text-xs">
                <div class="tio-flex-between">
                  <span class="tio-fg-muted">Department</span>
                  <span class="font-medium">Engineering</span>
                </div>
                <div class="tio-flex-between">
                  <span class="tio-fg-muted">TA Balance</span>
                  <span class="font-medium tio-fg-success">$3,750 remaining</span>
                </div>
                <div class="tio-flex-between">
                  <span class="tio-fg-muted">Active Apps</span>
                  <span class="font-medium">2 in review</span>
                </div>
              </div>
            </:body>
            <:footer>
              <div class="tio-flex-tight">
                <Button @intent="primary" @size="sm" class="flex-1">View Profile</Button>
                <Button
                  @intent="default"
                  @appearance="outlined"
                  @size="sm"
                  class="flex-1"
                >Message</Button>
              </div>
            </:footer>
          </Popover>

          {{! Notification popover }}
          <Popover
            @placement="bottom-end"
            @intent="default"
            @showCloseButton={{true}}
            @maxWidth={{380}}
          >
            <:trigger as |t|>
              <button
                type="button"
                {{t.register}}
                {{on "click" t.toggle}}
                class="relative flex items-center gap-1.5 px-3 py-1.5 rounded-lg tio-bg-sunken tio-fg-secondary tio-text-sm-medium hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              >
                <PhBell @size={{18}} />
                Notifications
                <span
                  class="absolute -top-1 -right-1 w-4 h-4 rounded-full tio-bg-danger tio-fg-inverse tio-text-micro font-bold tio-row-center"
                >3</span>
              </button>
            </:trigger>
            <:header>
              <div class="tio-row-between">
                <span>Notifications</span>
                <Chip @intent="danger" @size="sm">3 new</Chip>
              </div>
            </:header>
            <:body>
              <div class="divide-y divide-gray-100 dark:divide-gray-800 -mx-4 -my-3">
                <div
                  class="px-4 py-3 hover:tio-bg-sunken dark:hover:bg-gray-800/50 transition-colors"
                >
                  <div class="tio-flex">
                    <div
                      class="w-8 h-8 rounded-full tio-bg-success-soft dark:bg-emerald-900 tio-row-center shrink-0"
                    >
                      <PhCheckCircle @size={{14}} class="tio-fg-success" />
                    </div>
                    <div>
                      <p class="tio-text-sm-medium">Application Approved</p>
                      <p class="text-xs tio-fg-muted mt-0.5">Your CS 401 tuition was approved —
                        $2,500</p>
                      <p class="text-xs tio-fg-disabled mt-1">2 hours ago</p>
                    </div>
                  </div>
                </div>
                <div
                  class="px-4 py-3 hover:tio-bg-sunken dark:hover:bg-gray-800/50 transition-colors"
                >
                  <div class="tio-flex">
                    <div
                      class="w-8 h-8 rounded-full tio-bg-warning-soft dark:bg-amber-900 tio-row-center shrink-0"
                    >
                      <PhWarning @size={{14}} class="tio-fg-warning" />
                    </div>
                    <div>
                      <p class="tio-text-sm-medium">Document Needed</p>
                      <p class="text-xs tio-fg-muted mt-0.5">Upload grade report for Fall 2025</p>
                      <p class="text-xs tio-fg-disabled mt-1">Yesterday</p>
                    </div>
                  </div>
                </div>
                <div
                  class="px-4 py-3 hover:tio-bg-sunken dark:hover:bg-gray-800/50 transition-colors"
                >
                  <div class="tio-flex">
                    <div
                      class="w-8 h-8 rounded-full tio-bg-info-soft dark:bg-blue-900 tio-row-center shrink-0"
                    >
                      <PhInfo @size={{14}} class="tio-fg-info" />
                    </div>
                    <div>
                      <p class="tio-text-sm-medium">New Benefit Available</p>
                      <p class="text-xs tio-fg-muted mt-0.5">529 plan matching is now available</p>
                      <p class="text-xs tio-fg-disabled mt-1">3 days ago</p>
                    </div>
                  </div>
                </div>
              </div>
            </:body>
            <:footer>
              <div class="tio-flex-center">
                <Button @intent="primary" @appearance="minimal" @size="sm">View All Notifications</Button>
              </div>
            </:footer>
          </Popover>

          {{! Settings quick-actions }}
          <Popover @placement="bottom" @maxWidth={{240}}>
            <:trigger as |t|>
              <Button
                @intent="default"
                @appearance="outlined"
                {{t.register}}
                {{on "click" t.toggle}}
              >
                <PhGear @size={{16}} class="mr-1.5" />
                Quick Actions
              </Button>
            </:trigger>
            <:body>
              <div class="divide-y divide-gray-100 dark:divide-gray-800 -mx-4 -my-3">
                <button
                  type="button"
                  class="w-full tio-row px-4 py-2.5 text-sm tio-fg-secondary hover:tio-bg-sunken transition-colors text-left"
                >
                  <PhUser @size={{16}} class="tio-fg-disabled" />
                  Edit Profile
                </button>
                <button
                  type="button"
                  class="w-full tio-row px-4 py-2.5 text-sm tio-fg-secondary hover:tio-bg-sunken transition-colors text-left"
                >
                  <PhBell @size={{16}} class="tio-fg-disabled" />
                  Notification Preferences
                </button>
                <button
                  type="button"
                  class="w-full tio-row px-4 py-2.5 text-sm tio-fg-secondary hover:tio-bg-sunken transition-colors text-left"
                >
                  <PhGear @size={{16}} class="tio-fg-disabled" />
                  Account Settings
                </button>
              </div>
            </:body>
          </Popover>
        </div>
      </div>

      {{! ================================================================
            Section 8: API Reference
            ================================================================ }}
      <SectionHeader
        @title="API Reference"
        @subtitle="Arguments, blocks, and configuration options for the Popover component."
        @icon={{PhBookOpen}}
      />

      <div
        class="tio-bg-surface dark:bg-gray-900 rounded-xl border tio-border-default dark:border-gray-800 overflow-hidden"
      >
        <table class="w-full text-sm">
          <thead>
            <tr
              class="border-b tio-border-default dark:border-gray-800 tio-bg-sunken dark:bg-gray-800/50"
            >
              <th class="text-left px-4 py-3 font-semibold tio-fg-primary">Arg</th>
              <th class="text-left px-4 py-3 font-semibold tio-fg-primary">Type</th>
              <th class="text-left px-4 py-3 font-semibold tio-fg-primary">Default</th>
              <th class="text-left px-4 py-3 font-semibold tio-fg-primary">Description</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100 dark:divide-gray-800">
            <tr>
              <td class="px-4 py-2.5"><code
                  class="text-xs tio-bg-sunken px-1.5 py-0.5 rounded font-mono"
                >@placement</code></td>
              <td class="px-4 py-2.5 tio-fg-secondary font-mono text-xs">PopoverPlacement</td>
              <td class="px-4 py-2.5 font-mono text-xs">"bottom"</td>
              <td class="px-4 py-2.5 tio-fg-secondary">Preferred placement (auto-flips if needed)</td>
            </tr>
            <tr>
              <td class="px-4 py-2.5"><code
                  class="text-xs tio-bg-sunken px-1.5 py-0.5 rounded font-mono"
                >@triggerMode</code></td>
              <td class="px-4 py-2.5 tio-fg-secondary font-mono text-xs">"click" | "hover"</td>
              <td class="px-4 py-2.5 font-mono text-xs">"click"</td>
              <td class="px-4 py-2.5 tio-fg-secondary">How the popover is triggered</td>
            </tr>
            <tr>
              <td class="px-4 py-2.5"><code
                  class="text-xs tio-bg-sunken px-1.5 py-0.5 rounded font-mono"
                >@intent</code></td>
              <td class="px-4 py-2.5 tio-fg-secondary font-mono text-xs">PopoverIntent</td>
              <td class="px-4 py-2.5 font-mono text-xs">"default"</td>
              <td class="px-4 py-2.5 tio-fg-secondary">Color theme (default, info, success, warning,
                danger)</td>
            </tr>
            <tr>
              <td class="px-4 py-2.5"><code
                  class="text-xs tio-bg-sunken px-1.5 py-0.5 rounded font-mono"
                >@showAccentBar</code></td>
              <td class="px-4 py-2.5 tio-fg-secondary font-mono text-xs">boolean</td>
              <td class="px-4 py-2.5 font-mono text-xs">false</td>
              <td class="px-4 py-2.5 tio-fg-secondary">Colored bar on edge facing trigger</td>
            </tr>
            <tr>
              <td class="px-4 py-2.5"><code
                  class="text-xs tio-bg-sunken px-1.5 py-0.5 rounded font-mono"
                >@showCloseButton</code></td>
              <td class="px-4 py-2.5 tio-fg-secondary font-mono text-xs">boolean</td>
              <td class="px-4 py-2.5 font-mono text-xs">false</td>
              <td class="px-4 py-2.5 tio-fg-secondary">X button in top-right corner</td>
            </tr>
            <tr>
              <td class="px-4 py-2.5"><code
                  class="text-xs tio-bg-sunken px-1.5 py-0.5 rounded font-mono"
                >@maxWidth</code></td>
              <td class="px-4 py-2.5 tio-fg-secondary font-mono text-xs">number</td>
              <td class="px-4 py-2.5 font-mono text-xs">360</td>
              <td class="px-4 py-2.5 tio-fg-secondary">Max width in px</td>
            </tr>
            <tr>
              <td class="px-4 py-2.5"><code
                  class="text-xs tio-bg-sunken px-1.5 py-0.5 rounded font-mono"
                >@isOpen</code></td>
              <td class="px-4 py-2.5 tio-fg-secondary font-mono text-xs">boolean</td>
              <td class="px-4 py-2.5 font-mono text-xs">—</td>
              <td class="px-4 py-2.5 tio-fg-secondary">Controlled open state</td>
            </tr>
            <tr>
              <td class="px-4 py-2.5"><code
                  class="text-xs tio-bg-sunken px-1.5 py-0.5 rounded font-mono"
                >@onClose</code></td>
              <td class="px-4 py-2.5 tio-fg-secondary font-mono text-xs">() => void</td>
              <td class="px-4 py-2.5 font-mono text-xs">—</td>
              <td class="px-4 py-2.5 tio-fg-secondary">Called when popover closes</td>
            </tr>
            <tr>
              <td class="px-4 py-2.5"><code
                  class="text-xs tio-bg-sunken px-1.5 py-0.5 rounded font-mono"
                >@onOpen</code></td>
              <td class="px-4 py-2.5 tio-fg-secondary font-mono text-xs">() => void</td>
              <td class="px-4 py-2.5 font-mono text-xs">—</td>
              <td class="px-4 py-2.5 tio-fg-secondary">Called when popover opens</td>
            </tr>
          </tbody>
        </table>
      </div>

      {{! Blocks reference }}
      <div
        class="tio-bg-surface dark:bg-gray-900 rounded-xl border tio-border-default dark:border-gray-800 overflow-hidden mt-4"
      >
        <div
          class="px-4 py-3 border-b tio-border-default dark:border-gray-800 tio-bg-sunken dark:bg-gray-800/50"
        >
          <Heading @level={{4}}>Named Blocks</Heading>
        </div>
        <table class="w-full text-sm">
          <tbody class="divide-y divide-gray-100 dark:divide-gray-800">
            <tr>
              <td class="px-4 py-2.5 w-32"><code
                  class="text-xs tio-bg-brand-soft dark:bg-violet-900 tio-fg-brand dark:text-violet-300 px-1.5 py-0.5 rounded font-mono"
                >:trigger</code></td>
              <td class="px-4 py-2.5 tio-fg-secondary">
                <strong>Required.</strong>
                Yields
                <code class="text-xs tio-bg-sunken px-1 rounded">t.register</code>
                (modifier),
                <code class="text-xs tio-bg-sunken px-1 rounded">t.toggle</code>,
                <code class="text-xs tio-bg-sunken px-1 rounded">t.open</code>,
                <code class="text-xs tio-bg-sunken px-1 rounded">t.close</code>,
                <code class="text-xs tio-bg-sunken px-1 rounded">t.hoverHandlers</code>
                (modifier),
                <code class="text-xs tio-bg-sunken px-1 rounded">t.isOpen</code>
              </td>
            </tr>
            <tr>
              <td class="px-4 py-2.5"><code
                  class="text-xs tio-bg-brand-soft dark:bg-violet-900 tio-fg-brand dark:text-violet-300 px-1.5 py-0.5 rounded font-mono"
                >:header</code></td>
              <td class="px-4 py-2.5 tio-fg-secondary">Optional. Styled header with bottom border.</td>
            </tr>
            <tr>
              <td class="px-4 py-2.5"><code
                  class="text-xs tio-bg-brand-soft dark:bg-violet-900 tio-fg-brand dark:text-violet-300 px-1.5 py-0.5 rounded font-mono"
                >:body</code></td>
              <td class="px-4 py-2.5 tio-fg-secondary">Main content area. If omitted, uses default
                block.</td>
            </tr>
            <tr>
              <td class="px-4 py-2.5"><code
                  class="text-xs tio-bg-brand-soft dark:bg-violet-900 tio-fg-brand dark:text-violet-300 px-1.5 py-0.5 rounded font-mono"
                >:footer</code></td>
              <td class="px-4 py-2.5 tio-fg-secondary">Optional. Styled footer with top border.
                Great for action buttons.</td>
            </tr>
          </tbody>
        </table>
      </div>
    </VStack>
  </template>
}
